"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type NavigationPendingContextValue = {
  /** Destination path (no locale) while a client navigation is in flight — set immediately on sidebar pointer down + via useLinkStatus. */
  navigationTarget: string | null;
  /** Called from a descendant of `<Link>` (useLinkStatus). */
  setPendingFromLink: (pathAfterLocale: string, pending: boolean) => void;
  /** Clears any in-flight main-column overlay (e.g. after navigation completes or timeout). */
  clearNavigationPending: () => void;
};

export const NavigationPendingContext =
  createContext<NavigationPendingContextValue | null>(null);

export function NavigationPendingProvider({ children }: { children: ReactNode }) {
  const [navigationTarget, setNavigationTarget] = useState<string | null>(null);

  const setPendingFromLink = useCallback(
    (pathAfterLocale: string, pending: boolean) => {
      setNavigationTarget((prev) => {
        if (pending) return pathAfterLocale;
        if (prev === pathAfterLocale) return null;
        return prev;
      });
    },
    [],
  );

  const clearNavigationPending = useCallback(() => {
    setNavigationTarget(null);
  }, []);

  const value = useMemo(
    () => ({
      navigationTarget,
      setPendingFromLink,
      clearNavigationPending,
    }),
    [navigationTarget, setPendingFromLink, clearNavigationPending],
  );

  return (
    <NavigationPendingContext.Provider value={value}>
      {children}
    </NavigationPendingContext.Provider>
  );
}

export function useNavigationPending() {
  const ctx = useContext(NavigationPendingContext);
  if (!ctx) {
    throw new Error(
      "useNavigationPending must be used within NavigationPendingProvider",
    );
  }
  return ctx;
}
